// interfaces/bundleTypes.ts
/**
 * Bundle-related type definitions
 * These extend the existing participantTrackerTypes for bundle status tracking.
 *
 * Import and re-export from participantTrackerTypes.ts, or merge as needed.
 */

import { BundleData, BundleState, BundleStatusInfo, OverallBundleStatus } from '../utils/bundleUtils';
import { CombinedApiData } from './participantTrackerTypes';
import { Row } from 'react-table';

// Re-export bundle types for convenience
export { BundleState, OverallBundleStatus };
export type { BundleData, BundleStatusInfo };

// ============================================================
// EXTENDED API DATA
// ============================================================

/**
 * Extended participant data with parsed bundle information
 * This is what we'd ideally receive from an enhanced API
 */
export interface ParticipantBundleData {
  /** Participant identifier */
  participantId: string;

  /** Baseline (SV3) date as timestamp */
  baselineDate: number | null;

  /** Bundle data from server */
  bundles: ServerBundleData[];
}

/**
 * Server-provided bundle data structure
 * This represents what the API should return for each bundle
 */
export interface ServerBundleData {
  /** Bundle number (1-9 for home bundles, 0 for training/localizer) */
  bundleNumber: number;

  /** Unix timestamps of all tests in this bundle */
  testTimestamps: number[];

  /** Bundle start time (first test) */
  bundleUnixTime: number | null;

  /** Layout name used for this bundle */
  bundleLayoutName: string | null;

  /** Whether bundle is complete per server */
  isComplete: boolean;

  /** Whether bundle window is closed per server */
  windowClosed: boolean;
}

// ============================================================
// CELL PROPS
// ============================================================

/**
 * Props for StudyWeekCell
 */
export interface StudyWeekCellProps {
  row: Row<CombinedApiData>;
  /** Calculated bundle status (passed from parent or calculated in cell) */
  bundleStatus?: BundleStatusInfo;
}

/**
 * Props for BundleStatusCell
 */
export interface BundleStatusCellProps {
  row: Row<CombinedApiData>;
  bundleStatus?: BundleStatusInfo;
}

/**
 * Props for BundleGridCell
 */
export interface BundleGridCellProps {
  row: Row<CombinedApiData>;
  bundleStatus?: BundleStatusInfo;
  /** Whether to show year separator */
  showYearSeparator?: boolean;
  /** Whether to show compact view */
  compact?: boolean;
}

/**
 * Props for CurrentBundleCell
 */
export interface CurrentBundleCellProps {
  row: Row<CombinedApiData>;
  bundleStatus?: BundleStatusInfo;
}

// ============================================================
// ALERT TYPES (extend existing AlertType enum)
// ============================================================

/**
 * Bundle-specific alert types
 * Add these to the existing AlertType enum in participantTrackerTypes.ts
 */
export enum BundleAlertType {
  BUNDLE_EXPIRING_SOON = 'Bundle Expiring Soon',
  BUNDLE_EXPIRED = 'Bundle Expired',
  BUNDLE_STALE = 'No Active Bundle (Expected)',
  BEHIND_SCHEDULE = 'Behind Bundle Schedule',
  TESTS_AT_RISK = 'Tests At Risk (Expiring Bundle)',
}

/**
 * Bundle-specific alert structure
 */
export interface BundleAlert {
  type: BundleAlertType;
  bundleNumber?: number;
  message: string;
  severity: 'error' | 'warning' | 'info';
  daysRemaining?: number;
  testsAtRisk?: number;
}

// ============================================================
// COLUMN DEFINITIONS
// ============================================================

/**
 * New column IDs for bundle tracking
 * Add these to COLUMN_IDS in participantTrackerTypes.ts
 */
export const BUNDLE_COLUMN_IDS = {
  STUDY_WEEK: 'studyWeek',
  BUNDLE_STATUS: 'bundleStatus',
  CURRENT_BUNDLE: 'currentBundle',
  BUNDLE_GRID: 'bundleGrid',
  NEXT_MILESTONE: 'nextMilestone',
  BUNDLES_COMPLETE: 'bundlesComplete',
  BUNDLES_USABLE: 'bundlesUsable',
} as const;

// ============================================================
// FILTER OPTIONS
// ============================================================

/**
 * Filter options for bundle status column
 */
export const BUNDLE_STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: OverallBundleStatus.EXPIRING, label: 'Expiring Soon' },
  { value: OverallBundleStatus.AT_RISK, label: 'At Risk' },
  { value: OverallBundleStatus.BEHIND, label: 'Behind Schedule' },
  { value: OverallBundleStatus.STALE, label: 'Stale' },
  { value: OverallBundleStatus.ACTIVE, label: 'Active' },
  { value: OverallBundleStatus.ON_TRACK, label: 'On Track' },
  { value: OverallBundleStatus.IN_GAP, label: 'In Gap Period' },
  { value: OverallBundleStatus.NOT_STARTED, label: 'Not Started' },
] as const;

// ============================================================
// SORT FUNCTIONS
// ============================================================

/**
 * Sort priority for overall bundle status (for table sorting)
 * Lower number = higher priority (shown first when sorting desc)
 */
export const BUNDLE_STATUS_SORT_PRIORITY: Record<OverallBundleStatus, number> = {
  [OverallBundleStatus.EXPIRING]: 1,
  [OverallBundleStatus.AT_RISK]: 2,
  [OverallBundleStatus.BEHIND]: 3,
  [OverallBundleStatus.STALE]: 4,
  [OverallBundleStatus.ACTIVE]: 5,
  [OverallBundleStatus.ON_TRACK_PARTIAL]: 6,
  [OverallBundleStatus.ON_TRACK]: 7,
  [OverallBundleStatus.IN_GAP]: 8,
  [OverallBundleStatus.NOT_STARTED]: 9,
  [OverallBundleStatus.COMPLETE]: 10,
};

// ============================================================
// DISPLAY CONFIGURATION
// ============================================================

/**
 * Visual configuration for bundle states
 */
export const BUNDLE_STATE_VISUALS: Record<BundleState, {
  icon: string;
  bgColor: string;
  textColor: string;
  borderColor: string;
  label: string;
}> = {
  [BundleState.COMPLETE]: {
    icon: '✓',
    bgColor: 'bg-green-100',
    textColor: 'text-green-700',
    borderColor: 'border-green-500',
    label: 'Complete',
  },
  [BundleState.PARTIAL]: {
    icon: '◐',
    bgColor: 'bg-yellow-100',
    textColor: 'text-yellow-700',
    borderColor: 'border-yellow-500',
    label: 'Partial',
  },
  [BundleState.ACTIVE]: {
    icon: '●',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-700',
    borderColor: 'border-blue-500',
    label: 'Active',
  },
  [BundleState.EXPIRING]: {
    icon: '⏳',
    bgColor: 'bg-orange-100',
    textColor: 'text-orange-700',
    borderColor: 'border-orange-500',
    label: 'Expiring',
  },
  [BundleState.EXPIRED]: {
    icon: '✗',
    bgColor: 'bg-red-100',
    textColor: 'text-red-700',
    borderColor: 'border-red-500',
    label: 'Expired',
  },
  [BundleState.NOT_STARTED]: {
    icon: '○',
    bgColor: 'bg-red-50',
    textColor: 'text-red-600',
    borderColor: 'border-red-400',
    label: 'Not Started',
  },
  [BundleState.FUTURE]: {
    icon: '○',
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-500',
    borderColor: 'border-gray-300',
    label: 'Future',
  },
};

/**
 * Visual configuration for overall status
 */
export const OVERALL_STATUS_VISUALS: Record<OverallBundleStatus, {
  icon: string;
  colorScheme: string;
  label: string;
  description: string;
}> = {
  [OverallBundleStatus.COMPLETE]: {
    icon: '✓',
    colorScheme: 'green',
    label: 'Complete',
    description: 'All bundles completed',
  },
  [OverallBundleStatus.ON_TRACK]: {
    icon: '✓',
    colorScheme: 'green',
    label: 'On Track',
    description: 'Meeting all milestones',
  },
  [OverallBundleStatus.ON_TRACK_PARTIAL]: {
    icon: '◐',
    colorScheme: 'green',
    label: 'On Track',
    description: 'Meeting milestones (some partial)',
  },
  [OverallBundleStatus.ACTIVE]: {
    icon: '●',
    colorScheme: 'blue',
    label: 'Active',
    description: 'Bundle in progress',
  },
  [OverallBundleStatus.IN_GAP]: {
    icon: '—',
    colorScheme: 'gray',
    label: 'In Gap',
    description: 'Expected gap period',
  },
  [OverallBundleStatus.EXPIRING]: {
    icon: '⏳',
    colorScheme: 'orange',
    label: 'Expiring',
    description: 'Bundle expiring soon',
  },
  [OverallBundleStatus.AT_RISK]: {
    icon: '⚠',
    colorScheme: 'orange',
    label: 'At Risk',
    description: 'Behind schedule, bundle expiring',
  },
  [OverallBundleStatus.BEHIND]: {
    icon: '↓',
    colorScheme: 'red',
    label: 'Behind',
    description: 'Behind schedule',
  },
  [OverallBundleStatus.STALE]: {
    icon: '!',
    colorScheme: 'red',
    label: 'Stale',
    description: 'No activity when expected',
  },
  [OverallBundleStatus.NOT_STARTED]: {
    icon: '—',
    colorScheme: 'gray',
    label: 'Not Started',
    description: 'Pre-baseline',
  },
};
